import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

export default function ChangePasswordRequired() {
    const { user, changePassword, logout } = useAuth();
    const nav = useNavigate();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        if (next !== confirm) { setError("Passwords do not match"); return; }
        setBusy(true);
        try {
            await changePassword(current, next);
            toast.success("Password updated");
            nav("/admin", { replace: true });
        } catch (err) { setError(formatApiError(err)); }
        finally { setBusy(false); }
    }

    return (
        <div className="min-h-screen bg-[color:var(--gsf-green-900)] text-white flex items-center justify-center px-4" data-testid="change-password">
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-8"><Logo variant="full" size={110} className="rounded-xl bg-white p-2" /></div>
                <div className="bg-white text-[color:var(--gsf-charcoal)] rounded-2xl p-8 shadow-2xl">
                    <div className="gsf-eyebrow">Required</div>
                    <h1 className="mt-2 font-serif text-2xl text-[color:var(--gsf-green-700)] font-bold">Change your password</h1>
                    <p className="mt-2 text-sm text-gray-500">You must set a new password before continuing. Signed in as <strong>{user?.email}</strong>.</p>
                    <form onSubmit={onSubmit} className="mt-6 space-y-4">
                        <PasswordInput label="Current (temporary) password" value={current} onChange={setCurrent} testid="cp-current" />
                        <PasswordInput label="New password" value={next} onChange={setNext} testid="cp-new" hint="Min 12 chars, mix upper/lower/digit/symbol" />
                        <PasswordInput label="Confirm new password" value={confirm} onChange={setConfirm} testid="cp-confirm" />
                        {error && <p className="text-sm text-red-600" data-testid="cp-error">{error}</p>}
                        <button type="submit" disabled={busy} className="gsf-btn-primary w-full justify-center disabled:opacity-60" data-testid="cp-submit">
                            {busy ? "Updating…" : "Update password"}
                        </button>
                        <button type="button" onClick={logout} className="text-sm text-gray-500 hover:underline" data-testid="cp-logout">Sign out instead</button>
                    </form>
                </div>
            </div>
        </div>
    );
}

function PasswordInput({ label, value, onChange, testid, hint }) {
    return (
        <div>
            <label className="text-sm font-medium">{label}</label>
            <input type="password" required value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-3 focus:outline-none focus:border-[color:var(--gsf-green-700)]" />
            {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
        </div>
    );
}
